import { BehaviorSubject, Subscription } from 'rxjs';
import { environment } from 'src/environments/environment';
import { CandleStickService } from '../api/candle-stick.service';
import { FiatExchangeRateService } from '../api/fiat-exchange-rate.service';
import { MetadataService } from '../api/metadata.service';
import { WebSocketService } from '../api/web-socket.service';
import { ExcahngeRate } from '../models/exchange-rate';
import { cloneDeep as _cloneDeep } from 'lodash-es';
import { PriceDataPoint, PriceDataSeries, PriceChartDuration, CryptoPriceData } from '../models/price-chart';
import { WebSocketTicker } from '../models/wss';
import { Injectable } from '@angular/core';
import { ChartColor } from '../models/chart';

@Injectable()
export class CryptoPriceService {
  private _globalCurrencyRate: number;
  private _globalCurrency: string;
  private _baseCurrency: string;
  private _mainCurrency: string;
  private _ticker: WebSocketTicker;
  private _duration: PriceChartDuration;
  private _priceHistory: PriceDataSeries;

  private _tickerSubscription: Subscription;
  private _historySubscription: Subscription;
  private _changesSubject = new BehaviorSubject<CryptoPriceData>(this.data);

  public constructor(
    private fiatExchangeRateService: FiatExchangeRateService,
    private wss: WebSocketService,
    private candlesService: CandleStickService,
    private metadataService: MetadataService
  ) {
    this._globalCurrency = environment.currencies.globalCurrency;

    this.wss.connectionChanges.subscribe(() => {
      this.getTicker(this.pair);
    });
  }


  setCurrency(currency: string) {
    if (this._mainCurrency === currency) {
      return;
    }

    this._mainCurrency = currency;
    this.getTicker(this.pair);
    this.getPriceHistory(this.pair, this.duration);
  }

  setDuration(duration: PriceChartDuration) {
    this._duration = duration;
    this.getPriceHistory(this.pair, this.duration);
  }

  setBaseCurrency(currency: string) {
    if(this._baseCurrency === currency) {
      return;
    }

    this._baseCurrency = currency;
    this.getGlobalCurrencyRate(this._baseCurrency);
    this.getPriceHistory(this.pair, this.duration);
    this.getTicker(this.pair);
  }

  setBaseCurrencyToUserCurrency() {
    this.metadataService.userCurrency().subscribe(response => {
      this.setBaseCurrency(response);
    }, err => {
      this.setBaseCurrency(environment.currencies.defaultCurrency);
    });
  }

  getGlobalCurrencyRate(baseCurrency: string) {
    if (!baseCurrency) {
      return;
    }

    this._globalCurrencyRate = null;

    this.fiatExchangeRateService.getGlobalCurrencyRate(baseCurrency).subscribe(response => {
      this._globalCurrencyRate = response;
      this._changesSubject.next(this.data);
    }, err => {
      this._globalCurrencyRate = 0;
      this._changesSubject.next(this.data);
    })
  }

  getTicker(pair: string) {
    if (!this.pair) {
      return;
    }

    this._tickerSubscription?.unsubscribe();
    this._ticker = null;

    this._tickerSubscription = this.wss.ticker(pair).subscribe(response => {
      this._ticker = response;
      this._changesSubject.next(this.data);
    }, err => {
      this._ticker = null;
      this._changesSubject.next(this.data);
    });
  }

  getPriceHistory(pair: string, duration: PriceChartDuration) {
    if (!this.pair || !this.duration) {
      return;
    }

    this._historySubscription?.unsubscribe();
    this._priceHistory = null;

    this._historySubscription = this.candlesService.getPriceHistory(pair, duration).subscribe(response => {
      this._priceHistory = response;
      this._changesSubject.next(this.data);
    }, err => {
      this._priceHistory = null;
      this._changesSubject.next(this.data);
    })
  }

  get data(): CryptoPriceData {
    const data: CryptoPriceData = {
      pair: this.pair,
      mainCurrency: this.mainCurrency,
      isPositvitePriceChange: this.isPositvitePriceChange,
      priceChangePercentage: this.priceChangePercentage,
      chartColor: this.chartColor,
      priceHistory: this.priceHistory,
      livePriceHistory: this.livePriceHistory,
      baseCurrency: {
        currency: this.baseCurrency,
        lastPrice: this.baseCurrencyLastPrice?.amount,
        priceHistory: this.baseCurrencyPriceHistory,
        priceChange: this.baseCurrencyPriceChange,
      },
      globalCurrency: {
        currency: this.globalCurrency,
        lastPrice: this.globalCurrencyLastPrice?.amount,
        priceHistory: this.globalCurrencyPriceHistory,
        priceChange: this.globalCurrencyPriceChange,
      },
      defaultCurrency: null
    };

    data.defaultCurrency = this._globalCurrency ? data?.globalCurrency : data.baseCurrency;

    return data;
  }

  get valueChanges() {
    return this._changesSubject.asObservable();
  }

  get pair() {
    if (!this.mainCurrency || !this.baseCurrency) {
      return null;
    }

    return `${this.mainCurrency}-${this.baseCurrency}`;
  }

  get duration() {
    return this._duration;
  }

  get mainCurrency() {
    return this._mainCurrency;
  }

  get baseCurrency() {
    return this._baseCurrency;
  }

  get globalCurrency() {
    return this._globalCurrency;
  }

  get globalCurrencyRate() {
    return this._globalCurrencyRate;
  }

  get priceHistory() {
    return this._priceHistory;
  }

  get livePriceHistory() {
    const data = _cloneDeep(this._priceHistory);
    const time = (new Date()).getTime();
    const baseCurrencyLastPrice = this.baseCurrencyLastPrice?.amount;
    const globalCurrencyLastPrice = this.globalCurrencyLastPrice?.amount;

    if (data?.baseCurrencyData?.data && baseCurrencyLastPrice) {
      data.baseCurrencyData.data = [{
        x: time,
        y: baseCurrencyLastPrice
      }, ...data.baseCurrencyData.data];
    }

    if (data?.globalCurrencyData?.data && globalCurrencyLastPrice) {
      data.globalCurrencyData.data = [{
        x: time,
        y: globalCurrencyLastPrice
      }, ...data.globalCurrencyData.data];
    }

    if (data?.globalCurrencyData || data?.baseCurrencyData) {
      data.defaultCurrencyData = this._globalCurrency ? data.globalCurrencyData : data.baseCurrencyData;
    }

    return data;
  }

  get baseCurrencyPriceHistory() {
    return this._priceHistory?.baseCurrencyData?.data;
  }

  get globalCurrencyPriceHistory() {
    return this._priceHistory?.globalCurrencyData?.data;
  }

  get globalCurrencyLastPrice(): ExcahngeRate {
    return {
      currency: this._globalCurrency,
      amount: this._ticker?.lastPrice * this._globalCurrencyRate
    }
  }

  get baseCurrencyLastPrice(): ExcahngeRate {
    return {
      currency: this._baseCurrency,
      amount: this._ticker?.lastPrice
    }
  }

  get baseCurrencyPriceChange(): number {
    const oldestDataPoint = this.oldestBaseCurrencyDataPoint();
    return this._ticker?.lastPrice - oldestDataPoint?.y;
  }

  get globalCurrencyPriceChange(): number {
    const oldestDataPoint = this.oldestGlobalCurrencyDataPoint();
    return (this._ticker?.lastPrice * this.globalCurrencyRate) - oldestDataPoint?.y;
  }

  get priceChangePercentage() {
    const oldestDataPoint = this.oldestBaseCurrencyDataPoint();
    return (this.baseCurrencyPriceChange / oldestDataPoint?.y) * 100;
  }

  get isPositvitePriceChange() {
    return this.baseCurrencyPriceChange >= 0;
  }

  get chartColor(): ChartColor {
    return this.baseCurrencyPriceChange < 0 ? ChartColor.Negative : ChartColor.Positive
  }

  private oldestBaseCurrencyDataPoint(): PriceDataPoint {
    const lastId = this?._priceHistory?.baseCurrencyData?.data?.length - 1;
    return this._priceHistory?.baseCurrencyData?.data?.[lastId];
  }

  private oldestGlobalCurrencyDataPoint(): PriceDataPoint {
    const lastId = this?._priceHistory?.globalCurrencyData?.data?.length - 1;
    return this._priceHistory?.globalCurrencyData?.data?.[lastId];
  }

  ngOnDestroy() {
    this._tickerSubscription?.unsubscribe();
    this._changesSubject.complete();
  }
}
